<template>
  <div class="space-y-4">
    <h3 class="text-lg font-medium text-center mb-4">{{ $t('completeProfile') }}</h3>
    
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">{{ $t('phoneNumber') }}</label>
        <div style="direction: ltr;" class="px-4 py-2 bg-gray-100 rounded-[32px] h-14 flex items-center text-gray-600">
          {{ phone }}
        </div>
      </div>

      <div v-if="requireEmail" class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">{{ $t('email') }} *</label>
        <input
          type="email"
          v-model="formData.email"
          required
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :placeholder="$t('email')"
        />
      </div>

      <div v-if="requireFirstName" class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">{{ $t('firstName') }} *</label>
        <input
          type="text"
          v-model="formData.first_name"
          required
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :placeholder="$t('firstName')"
        />
      </div>

      <div v-if="requireLastName" class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">{{ $t('lastName') }} *</label>
        <input
          type="text"
          v-model="formData.last_name"
          required
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :placeholder="$t('lastName')"
        />
      </div>

      <div v-for="field in customFields" :key="field.meta_key" class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">
          {{ field.name }}
          <span v-if="field.status === 'required'" class="text-red-500">*</span>
        </label>
        
        <!-- Text input -->
        <input
          v-if="field.type === 'text' || !field.type"
          type="text"
          v-model="formData.custom_fields[field.meta_key]"
          :required="field.status === 'required'"
          :maxlength="field.max_length"
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :placeholder="field.name"
        />
        
        <!-- Number input -->
        <input
          v-else-if="field.type === 'number'"
          type="number"
          v-model="formData.custom_fields[field.meta_key]"
          :required="field.status === 'required'"
          :max="field.max_length"
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :placeholder="field.name"
        />
        
        <!-- Textarea -->
        <textarea
          v-else-if="field.type === 'textarea'"
          v-model="formData.custom_fields[field.meta_key]"
          :required="field.status === 'required'"
          :maxlength="field.max_length"
          rows="4"
          class="appearance-none rounded-[20px] relative block w-full px-3 py-2 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :placeholder="field.name"
        ></textarea>
        
        <!-- Select dropdown -->
        <select
          v-else-if="field.type === 'select'"
          v-model="formData.custom_fields[field.meta_key]"
          :required="field.status === 'required'"
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <option value="">{{ $t('pleaseSelect') }}</option>
          <option v-for="option in getFieldOptions(field)" :key="option" :value="option">{{ option }}</option>
        </select>
        
        <!-- Checkbox -->
        <div v-else-if="field.type === 'checkbox'" class="flex items-start">
          <div class="flex items-center h-5">
            <input
              type="checkbox"
              v-model="formData.custom_fields[field.meta_key]"
              :required="field.status === 'required'"
              class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
          </div>
          <div class="ml-3 text-sm">
            <label class="font-medium text-gray-700">{{ field.checkbox_label || field.name }}</label>
          </div>
        </div>
        
        <!-- Radio buttons -->
        <div v-else-if="field.type === 'radio'" class="space-y-2">
          <div v-for="option in getFieldOptions(field)" :key="option" class="flex items-center">
            <input
              type="radio"
              :name="field.meta_key"
              :value="option"
              v-model="formData.custom_fields[field.meta_key]"
              :required="field.status === 'required'"
              class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
            />
            <label class="ml-3 block text-sm font-medium text-gray-700">
              {{ option }}
            </label>
          </div>
        </div>
        
        <!-- Character count for text and textarea -->
        <div v-if="(field.type === 'text' || field.type === 'textarea') && field.max_length" class="text-xs text-gray-500 text-right">
          {{ formData.custom_fields[field.meta_key]?.length || 0 }}/{{ field.max_length }}
        </div>
      </div>

      <button
        type="submit"
        class="w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-[32px] h-14 text-white bg-main hover:contrast-150 focus:outline-none focus:ring-0"
        :disabled="loading"
      >
        <span v-if="loading" class="spinner-loading"></span>
        <span v-else>{{ $t('continue') }}</span>
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { showError } from '../../utils/swal'
import { t } from '../../utils/translate'

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  },
  requireEmail: {
    type: Boolean,
    default: false
  },
  requireFirstName: {
    type: Boolean,
    default: false
  },
  requireLastName: {
    type: Boolean,
    default: false
  },
  customFields: {
    type: Array,
    default: () => []
  },
  phone: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['submit'])
const formData = ref({
  email: '',
  first_name: '',
  last_name: '',
  custom_fields: {}
})

const getFieldOptions = (field) => {
  if (!field.options) return []
  return field.options.split(',').map(option => option.trim()).filter(option => option)
}

const handleSubmit = () => {
  if (props.requireEmail && !formData.value.email) {
    showError(t('emailRequired'))
    return
  }
  
  if (props.requireFirstName && !formData.value.first_name) {
    showError(t('firstNameRequired'))
    return
  }
  
  if (props.requireLastName && !formData.value.last_name) {
    showError(t('lastNameRequired'))
    return
  }
  
  emit('submit', formData.value)
}
</script> 